import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, CheckCircle2 } from "lucide-react";
import { Input, Consent, validateContact } from "@/components/site/Form";
import { api, apiError, gbp, imgUrl, miles } from "@/lib/api";
import { useSite } from "@/lib/site";
import { useSeo } from "@/hooks/useSeo";

export default function Reserve() {
  const { slug } = useParams();
  const nav = useNavigate();
  const site = useSite();
  const [v, setV] = useState(null);
  const [f, setF] = useState({ name: "", email: "", phone: "", consent: false });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  useSeo({ title: "Reserve a vehicle | Cirencester 4x4", path: `/reserve/${slug}` });
  useEffect(() => { api.get(`/vehicles/${slug}`).then((r) => setV(r?.data || null)).catch(() => setV(false)); }, [slug]);

  if (v === false) return <main className="px-5 pb-24 pt-40 text-center"><h1 className="font-serif text-4xl">Vehicle not found</h1><Link to="/stock" className="btn btn-primary mt-6">View Stock</Link></main>;
  if (!v) return <main className="min-h-screen pt-40" />;
  const amount = v.reservation_amount || site.reservation_amount || 250;
  const available = ["available", "just_arrived"].includes(v.status);
  const bind = (k) => ({ value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }), error: errors[k] });

  const submit = async (e) => {
    e.preventDefault();
    const er = validateContact(f);
    if (er.consent) er.consent = "Please confirm you have read the reservation terms";
    setErrors(er);
    if (Object.keys(er).length) return;
    setBusy(true);
    try {
      const r = await api.post("/reservations", { ...f, vehicle_id: v.id });
      nav(`/reserve/confirmation/${r.data.id}`);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <main className="mx-auto max-w-[1200px] px-5 pb-28 pt-32 sm:px-8 lg:pt-40" data-testid="reserve-page">
      <p className="eyebrow text-forest">Reserve</p>
      <h1 className="mt-4 font-serif text-5xl text-charcoal sm:text-6xl">Reserve this vehicle</h1>
      <p className="mt-4 max-w-2xl font-serif text-2xl italic text-forest">Reserve this vehicle while we arrange the next steps with you.</p>
      <div className="mt-14 grid gap-12 lg:grid-cols-12">
        <aside className="lg:col-span-5">
          <div className="border border-stone" data-testid="reserve-summary">
            <img src={imgUrl(v.images?.[0])} alt={`${v.make} ${v.model}`} className="aspect-[3/2] w-full object-cover" />
            <div className="p-6">
              <p className="eyebrow text-charcoal/75">{v.year} · {v.make}</p>
              <p className="mt-1 font-serif text-3xl">{v.model}</p>
              <p className="text-sm text-charcoal/80">{v.derivative}</p>
              <div className="mt-4 flex justify-between border-t border-stone pt-4 text-sm"><span>{miles(v.mileage)}</span><span className="font-serif text-2xl text-forest">{gbp(v.price)}</span></div>
              <div className="mt-4 flex justify-between bg-stone/50 px-4 py-3 text-sm"><span>Reservation amount</span><strong data-testid="reserve-amount">{gbp(amount)}</strong></div>
            </div>
          </div>
          <div className="mt-6 border-l-2 border-forest pl-5 text-sm leading-relaxed text-charcoal/80" data-testid="reserve-policy">
            <p className="mb-2 font-semibold text-charcoal">Reservation policy</p>
            <p>{site.reservation_terms}</p>
            <Link to="/legal/reservations" className="mt-2 inline-block underline">Read the reservation terms</Link>
          </div>
        </aside>
        <div className="lg:col-span-6 lg:col-start-7">
          {!available ? (
            <div className="border border-stone p-8"><p className="font-serif text-3xl">This vehicle is not currently available to reserve.</p><Link to={`/stock/${v.slug}`} className="btn btn-secondary mt-6">Back to vehicle</Link></div>
          ) : (
            <form noValidate onSubmit={submit} className="space-y-6" data-testid="reserve-form">
              <h2 className="font-serif text-3xl">Your details</h2>
              <div className="grid gap-5 sm:grid-cols-2">
                <Input id="r-name" label="Name" required autoComplete="name" {...bind("name")} className="sm:col-span-2" />
                <Input id="r-email" label="Email" type="email" required autoComplete="email" {...bind("email")} />
                <Input id="r-phone" label="Telephone" type="tel" required autoComplete="tel" {...bind("phone")} />
              </div>
              <div className="border border-dashed border-stone-dark bg-[#FBF9F4] p-6" data-testid="payment-placeholder">
                <div className="flex items-center gap-3"><CreditCard className="h-5 w-5 text-forest" strokeWidth={1.5} /><p className="font-semibold">Payment</p></div>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/80">Online payment is not yet connected. <strong>No payment will be taken.</strong> We will contact you to confirm the reservation and arrange the next steps. The vehicle is marked reserved only once the reservation is confirmed.</p>
              </div>
              <Consent id="r-consent" checked={f.consent} onChange={(val) => setF({ ...f, consent: val })} error={errors.consent}>
                I have read the reservation policy and agree to Cirencester 4x4 using my details as described in the <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.
              </Consent>
              <button type="submit" disabled={busy} className="btn btn-primary w-full sm:w-auto" data-testid="reserve-submit">{busy ? "Sending…" : "Request reservation"}</button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}

export function ReserveConfirmation() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  useSeo({ title: "Reservation received | Cirencester 4x4" });
  useEffect(() => { api.get(`/reservations/${id}`).then((x) => setR(x?.data || null)).catch(() => setR(false)); }, [id]);
  return (
    <main className="mx-auto max-w-3xl px-5 pb-28 pt-40 text-center" data-testid="reserve-confirmation">
      <CheckCircle2 className="mx-auto h-12 w-12 text-forest" strokeWidth={1} />
      <h1 className="mt-6 font-serif text-5xl">Reservation request received</h1>
      {r && <p className="mt-4 text-lg text-charcoal/80">Thank you{r.name ? `, ${r.name}` : ""}. We have received your request to reserve the <strong>{r.vehicle_title}</strong>.</p>}
      <p className="mt-4 text-charcoal/80">No payment has been taken. We will be in touch shortly to confirm and arrange the next steps.</p>
      {r && <p className="mt-2 text-xs uppercase tracking-[0.16em] text-charcoal/70" data-testid="reserve-ref">Reference: {String(r.id).slice(-8).toUpperCase()}</p>}
      <div className="mt-10 flex justify-center gap-3">
        {r && <Link to={`/stock/${r.vehicle_slug}`} className="btn btn-secondary">Back to vehicle</Link>}
        <Link to="/stock" className="btn btn-primary">View Stock</Link>
      </div>
    </main>
  );
}
